import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { SupportReportContextLineDto, SupportReportResponseDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';

type Req = {
  attachment?: { content: string; fileName: string };
  description: string;
  subject?: string;
};

type Return = {
  isSubmitting: boolean;
  submitFeedback: (req: Req) => Promise<SupportReportResponseDto>;
};

const browserContext = (): SupportReportContextLineDto[] => [
  { label: 'Przeglądarka', value: navigator.userAgent },
  { label: 'Okno', value: `${window.innerWidth}×${window.innerHeight}` },
  { label: 'Język', value: navigator.language },
  { label: 'Środowisko', value: window.location.host },
];

const toBase64 = (text: string): string =>
  btoa(String.fromCharCode(...new TextEncoder().encode(text)));

export const useSubmitFeedback = (): Return => {
  const { supportApi } = useApiClient();
  const { userId } = useUserContext();

  const { isPending, mutateAsync } = useMutation<SupportReportResponseDto, AxiosError, Req>({
    mutationFn: async req => {
      const { data } = await supportApi.reportSupportIssue(userId, {
        appUrl: window.location.href,
        attachment: req.attachment
          ? {
              contentBase64: toBase64(req.attachment.content),
              contentType: 'text/plain',
              fileName: req.attachment.fileName,
            }
          : undefined,
        context: browserContext(),
        description: req.description,
        subject: req.subject,
      });
      return data;
    },
    mutationKey: [QueryKeyEnum.SUPPORT.REPORT],
  });

  return {
    isSubmitting: isPending,
    submitFeedback: mutateAsync,
  };
};
